import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { DEFAULT_HIDE_DELAY, DEFAULT_MAX_TOASTS_NUMBER, DEFAULT_MAX_TOASTS_NUMBER_MOBILE, hidableToasts } from '../../internal/constants/toast';
import { calculateHideDelay, findOldestToastIdToHide, getIsMaxToastsNumberExceeded, getToastPlacement } from '../internal/utils';
import { useDeviceType } from '../../responsive/hooks/useDeviceType';
import { useUiKitToastApiContext, useUiKitToastsContext } from '../../internal/context';
export function useToast() {
  var initOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var placement = initOptions.placement,
    _initOptions$size = initOptions.size,
    size = _initOptions$size === void 0 ? 'M' : _initOptions$size,
    maxToastsNumber = initOptions.maxToastsNumber,
    _initOptions$hideDela = initOptions.hideDelay,
    hideDelay = _initOptions$hideDela === void 0 ? DEFAULT_HIDE_DELAY : _initOptions$hideDela,
    indent = initOptions.indent;
  var deviceType = useDeviceType();
  var toastsContext = useUiKitToastsContext();
  var toastsApiContext = useUiKitToastApiContext();
  var isMobileDevice = deviceType === 'phone';
  var placementFinal = getToastPlacement(placement, isMobileDevice);
  var maxToastsNumberFinal = maxToastsNumber !== null && maxToastsNumber !== void 0 ? maxToastsNumber : isMobileDevice ? DEFAULT_MAX_TOASTS_NUMBER_MOBILE : DEFAULT_MAX_TOASTS_NUMBER;
  var currentToastsStackApi = toastsContext.toastsStacksApi[placementFinal];
  var setToastsStack = currentToastsStackApi[1];
  var hide = React.useCallback(function (id) {
    setToastsStack(function (currentToastsStack) {
      var toastIndexToHide = currentToastsStack.findIndex(function (toast) {
        return toast.id === id;
      });
      if (toastIndexToHide !== -1) {
        currentToastsStack[toastIndexToHide].hidden = true;
        return _toConsumableArray(currentToastsStack);
      }
      return currentToastsStack;
    });
  }, [setToastsStack]);
  var hideWithDelay = React.useCallback(function (id, toastHideDelay) {
    var delay = calculateHideDelay(toastHideDelay, hideDelay);
    var timerId = window.setTimeout(function () {
      return hide(id);
    }, delay);
    return timerId;
  }, [hide, hideDelay]);
  var hideAll = React.useCallback(function () {
    setToastsStack(function (currentToastsStack) {
      currentToastsStack.forEach(function (toast) {
        return hide(toast.id);
      });
      return _toConsumableArray(currentToastsStack);
    });
  }, [setToastsStack, hide]);
  var update = React.useCallback(function (id, updateData) {
    setToastsStack(function (currentToastsStack) {
      var toastIndexToUpdate = currentToastsStack.findIndex(function (toast) {
        return toast.id === id;
      });
      if (toastIndexToUpdate !== -1) {
        var currentToast = currentToastsStack[toastIndexToUpdate];
        Object.keys(updateData).forEach(function (key) {
          currentToast[key] = updateData[key];
        });
        return _toConsumableArray(currentToastsStack);
      }
      return currentToastsStack;
    });
  }, [setToastsStack]);
  var open = React.useCallback(function (toast) {
    var id = String(Date.now());
    setToastsStack(function (currentStack) {
      /**
       * Тосты могу появляться либо сверху вниз ("top-center", "top-right"), либо снизу вверх ("bottom-center", "bottom-right").
       * От этого зависит то, в каком порядке они должны добавляться в стек/удаляться из стека.
       */
      var appearsFromBottomToTop = placementFinal.includes('bottom');

      /**
       * Проверяем, переполнен ли стек тостов. Если да, то скрываем самый старый тост.
       */
      var isMaxToastsNumberExceeded = getIsMaxToastsNumberExceeded(currentStack, maxToastsNumberFinal);
      if (isMaxToastsNumberExceeded) {
        var oldestVisibleToastId = findOldestToastIdToHide(currentStack, appearsFromBottomToTop);
        if (oldestVisibleToastId) {
          hide(oldestVisibleToastId);
        }
      }

      /**
       * Проверяем, является ли тост скрываемым. Если да, то инициируем его скрытие по таймауту, сохраняем id таймера.
       */
      var isHidable = hidableToasts.includes(toast.type) && (toast.autoHide === undefined || toast.autoHide);
      var timerId;
      if (isHidable) {
        timerId = hideWithDelay(id, toast.hideDelay);
      }
      var nextToast = _objectSpread(_objectSpread({
        size: size
      }, toast), {}, {
        id: id,
        placement: placementFinal,
        timerId: timerId,
        isHidable: isHidable
      });
      return appearsFromBottomToTop ? [].concat(_toConsumableArray(currentStack), [nextToast]) : [nextToast].concat(_toConsumableArray(currentStack));
    });
    return id;
  }, [setToastsStack, hide, hideWithDelay, maxToastsNumberFinal, placementFinal, size]);
  var api = React.useMemo(function () {
    return {
      open: open,
      hide: hide,
      hideWithDelay: hideWithDelay,
      hideAll: hideAll,
      update: update
    };
  }, [open, hide, hideWithDelay, hideAll, update]);
  React.useEffect(function () {
    toastsApiContext.setToastApiByPlacement(api, placementFinal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, placementFinal]);
  React.useEffect(function () {
    if (indent) {
      toastsApiContext.setIndentByPlacement(placementFinal, indent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementFinal, indent]);
  return api;
}